import type { MessageCreateRequest } from "@/api/types";
import { Button } from "@/components/Button/Button";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Announcement03Icon } from "@/components/Icons/Announcement03Icon";
import { MessageChatCircleIcon } from "@/components/Icons/MessageChatCircleIcon";
import { MessageTextCircle01Icon } from "@/components/Icons/MessageTextCircle01Icon";
import { Phone01Icon } from "@/components/Icons/Phone01Icon";
import { Share06Icon } from "@/components/Icons/Share06Icon";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { Label } from "@/components/Label/Label";
import { LinkFormatter } from "@/components/LinkFormatter/LinkFormatter";
import { DocumentMedia } from "@/components/Media/DocumentMedia";
import { Modal } from "@/components/Modal/Modal";
import { Notice } from "@/components/Notice/Notice";
import { Phone } from "@/components/Phone/Phone";
import { PhoneInner } from "@/components/Phone/PhoneInner";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import type { FormVideo, InputFile } from "@/components/VideoInput/useVideoInput";
import { useBool } from "@/hooks/useBool";
import { CommunityItemGallery } from "@/modules/messages/pages/List/components/CommunityItemMedia/CommunityItemGallery";
import { CommunityItemVideoForAppPreview } from "@/modules/messages/pages/List/components/CommunityItemMedia/CommunityItemVideoForAppPreview";
import { useTranslation } from "@/translations";

interface CreatePostAppPreviewProps {
  title: string;
  content: string;
  images: FormImage[];
  videos: FormVideo[];
  documents: FormDocument[];
  groupName: string | undefined;
  postType: MessageCreateRequest["type"] | undefined;
}
export function CreatePostAppPreview({
  title,
  content,
  images,
  videos,
  documents,
  groupName,
  postType,
}: CreatePostAppPreviewProps): React.ReactNode {
  const [isModalOpened, modalHandler] = useBool(false);
  const { t } = useTranslation();
  const wrapText = (text: string, maxWidth: number) => {
    return text.split("\n").flatMap((line) => {
      const wrapped = [];
      for (let i = 0; i < line.length; i += maxWidth) {
        wrapped.push(line.slice(i, i + maxWidth));
      }

      return wrapped;
    });
  };
  const getLineCount = () => {
    // 30 characters per line for title
    const wrappedTitle = wrapText(title, 30);
    // 50 characters per line for content
    const wrappedContent = wrapText(content, 50);

    return wrappedTitle.length + wrappedContent.length;
  };
  const isPostLengthWarningVisible = getLineCount() > 12;
  const previewSection = (
    <div className="mx-auto flex w-full max-w-72 flex-col">
      <Phone>
        <PhoneInner noBackground>
          <div className="relative flex min-h-full flex-1 flex-col items-center overflow-hidden bg-aop-off-white pb-4">
            <span className="fixed w-full border-b border-grey-300 bg-aop-off-white pb-2 pt-9 text-center text-caption-bold text-aop-dark-blue-500">
              {t("component.create-post-app-preview.community")}
            </span>
            <div className="flex w-full flex-col gap-4 overflow-y-auto bg-white p-4 pb-0 pt-20 shadow-sm">
              {(postType === "announcement" || postType === "announcementReadOnly") && (
                <div className="flex items-center gap-2">
                  <Label theme="purple" size="sm">
                    <Announcement03Icon />
                    <span>{t("component.create-post-app-preview.announcement")}</span>
                  </Label>
                </div>
              )}

              <div className="flex grow items-center">
                <div className="size-8 shrink-0">
                  <UserAvatar hideDeletedIcon />
                </div>
                <div className="ml-2">
                  <span className="line-clamp-1 flex items-center">
                    <span className="text-overline-bold text-black">
                      {t("component.create-post-app-preview.username")}
                      {groupName && (
                        <span className="text-overline-bold text-aop-dark-blue-500">
                          {` ${t("component.community-post.posted-in-group", {
                            group: groupName,
                          })}`}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="mt-1 line-clamp-1 text-[10px] text-grey-700">
                    <FormattedDate format="datetime" date={new Date()} />
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {title && <span className="text-caption-bold">{title}</span>}
                {content && (
                  <span className="w-full whitespace-pre-line text-[10px] leading-[14px]">
                    <LinkFormatter>{content.trim()}</LinkFormatter>
                  </span>
                )}
              </div>
              {images && images.length > 0 && (
                <div className="flex w-full justify-center overflow-hidden rounded-lg [&>div]:overflow-auto [&>div]:rounded-none">
                  <CommunityItemGallery
                    {...{
                      images,
                    }}
                  />
                </div>
              )}
              {videos && videos.length > 0 && <CommunityItemVideoForAppPreview video={videos[0] as InputFile} />}
              {documents && documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {documents.map((document, idx) => (
                    <DocumentMedia
                      key={idx}
                      {...{
                        document,
                      }}
                    />
                  ))}
                </div>
              )}
              {content && (
                <span className="text-[10px] text-grey-700">{t("component.create-post-app-preview.translate")}</span>
              )}
              <span className="text-[10px] text-grey-700">{t("component.create-post-app-preview.first-like")}</span>
              <div className="flex w-full items-center justify-between border-t border-grey-100 py-1 text-[10px] text-grey-700">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    <ThumbsUpIcon />
                    <span>{t("component.create-post-app-preview.like")}</span>
                  </div>
                  {(postType === "announcement" || postType === "undefined") && (
                    <>
                      <div className="flex items-center gap-0.5">
                        <MessageTextCircle01Icon />
                        <span>{t("component.create-post-app-preview.comment")}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <MessageChatCircleIcon />
                        <span className=" text-grey-700">0</span>
                      </div>
                    </>
                  )}
                </div>
                <Share06Icon />
              </div>
            </div>
          </div>
        </PhoneInner>
      </Phone>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <div className="hidden xl:block">
        <div className="flex size-full flex-col gap-4">
          {previewSection}
          {isPostLengthWarningVisible && (
            <Notice icon={<AlertTriangleIcon />} message={t("component.create-post-app-preview.warning")} />
          )}
        </div>
      </div>
      {/* Mobile */}
      <Button styling="tertiary" icon={<Phone01Icon />} className="xl:hidden" onClick={modalHandler.setTrue}>
        {t("page.admin-create-post.section.preview")}
      </Button>
      <Modal.Root isOpened={isModalOpened} onOpenChange={modalHandler.set}>
        <div className="flex flex-col gap-4">
          {previewSection}
          {isPostLengthWarningVisible && (
            <Notice icon={<AlertTriangleIcon />} message={t("component.create-post-app-preview.warning")} />
          )}
        </div>
      </Modal.Root>
    </>
  );
}
